"use no memo";

/* eslint-disable complexity */
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useLocation, useSearchParams } from 'react-router';

import { UserContext } from 'context/UserContext';

import { isAbortError } from 'helpers/api';
import { getOrder, updateOrderTip } from 'helpers/orderApi';
import { getOrderIdFromUrl } from 'helpers/url';

import { OnAddItemType } from 'types/menu';
import type { SubmittedOrderType } from 'types/order';

export const POST_CHECKOUT_ORDER_STATE_LOADING = 'loading';
export const POST_CHECKOUT_ORDER_STATE_LOADED = 'loaded';
export const POST_CHECKOUT_ORDER_STATE_ERRORED = 'errored';
export type PostCheckoutOrderState =
  | typeof POST_CHECKOUT_ORDER_STATE_LOADING
  | typeof POST_CHECKOUT_ORDER_STATE_LOADED
  | typeof POST_CHECKOUT_ORDER_STATE_ERRORED;

export type PostCheckoutOrderType = Omit<SubmittedOrderType, 'items'> & {
  items: OnAddItemType[];
};

export const PostCheckoutOrderContext = React.createContext<{
  error?: { code?: string; message?: string };
  fetchOrder: (
    orderId: string,
    guid: string | null | undefined,
    controller?: AbortController
  ) => Promise<void>;
  order: PostCheckoutOrderType | undefined;
  refetch: (controller?: AbortController) => void;
  state: PostCheckoutOrderState;
  updateTip: (newTip: number) => void;
}>({
  fetchOrder: () => {
    throw new Error('not implemented');
  },
  order: undefined,
  refetch() {
    throw new Error('not implemented');
  },
  state: POST_CHECKOUT_ORDER_STATE_LOADING,
  updateTip() {
    throw new Error('not implemented');
  },
});

export type PostCheckoutOrderContextType = React.ContextType<
  typeof PostCheckoutOrderContext
>;

export function formatOrderItems(
  orderItems: SubmittedOrderType['items']
): OnAddItemType[] {
  return orderItems.map((orderItem, index) => ({
    ...orderItem,

    itemIndex: index,

    /*
     * 'special_instructions' once an order is placed includes order ahead info (if exists) for first
     * item in order. 'instructions' contains the original special_instructions for that item
     */
    specialInstructions: orderItem.special_instructions,
    // we are sharing types between the API model side and the ordering data side
    // and this is only necessary on the API model side for e.g. the MenuItemModal
    modifierCategories: [],
    modifierNames: orderItem.modifier_categories.flatMap((modifer) =>
      modifer.selections.map((selection) => selection.name)
    ),
  }));
}

export default function PostCheckoutOrderProvider({
  children,
}: React.PropsWithChildren<never>) {
  const [order, setOrder] = useState<PostCheckoutOrderType>();
  const [error, setError] = useState<{ code?: string; message?: string }>();
  const [state, setState] = useState<PostCheckoutOrderState>(
    POST_CHECKOUT_ORDER_STATE_LOADING
  );
  const [searchParams] = useSearchParams();
  const paramsGuid = searchParams.get('guid');
  const { isLoggedIn } = useContext(UserContext);

  const location = useLocation();
  // $TSFIXME remove type when helpers/url is converted to TS
  const urlOrderId: string | undefined = getOrderIdFromUrl(location.pathname);

  const fetchOrder = useCallback<PostCheckoutOrderContextType['fetchOrder']>(
    async (orderId, guid, controller) => {
      setError(undefined);

      const response = await getOrder(orderId, guid, { controller });

      if (response.error) {
        if (isAbortError(response.error)) {
          // In event the request was aborted, do not set that as an error
          return;
        }

        setState(POST_CHECKOUT_ORDER_STATE_ERRORED);
        setError(response.error);
        return;
      }

      setState(POST_CHECKOUT_ORDER_STATE_LOADED);
      setOrder({
        // $TSFIXME: this should automatically narrow... idk why it's not
        ...response.data!,
        items: formatOrderItems(response.data!.items ?? []),
      });
    },
    []
  );

  const refetch = useCallback<PostCheckoutOrderContextType['refetch']>(
    (controller) => {
      if (!order) {
        return;
      }

      void fetchOrder(order.id, paramsGuid, controller);
    },
    [order, paramsGuid]
  );

  const updateTip = useCallback(
    (newTip: number) => {
      if (!order) {
        return;
      }

      void updateOrderTip(order.id, newTip);
    },
    [order]
  );

  useEffect(() => {
    if (!urlOrderId) {
      return () => {};
    }

    const controller = new AbortController();

    setState(POST_CHECKOUT_ORDER_STATE_LOADING);
    void fetchOrder(urlOrderId, paramsGuid, controller);

    return () => {
      controller.abort();
    };
  }, [urlOrderId, paramsGuid, isLoggedIn]);

  const mValue = useMemo(
    () => ({ error, fetchOrder, order, refetch, state, updateTip }),
    [error, fetchOrder, order, refetch, state]
  );

  return (
    <PostCheckoutOrderContext.Provider value={mValue}>
      {children}
    </PostCheckoutOrderContext.Provider>
  );
}
